<script setup lang="ts">
import { computed, ref, inject, onMounted } from 'vue'
import BbelField from '@/views/builder/components/bbel/BbelField.vue'
import type { UrlEncodedBodyContent } from '@/types'
import { BBEL_SCOPE_KEY, EMPTY_BBEL_SCOPE, type BbelScopeContext } from '@/views/builder/composables/bbel-context'
import type { ScopeDescriptor } from '@/types/bbel-types'

const props = defineProps<{
  modelValue: UrlEncodedBodyContent
}>()

const emit = defineEmits<{
  'update:modelValue': [value: UrlEncodedBodyContent]
}>()

// #5683 — field values are BBEL fields (scope provided by BuilderView; empty in read-only hosts).
const bbelScopeCtx = inject<BbelScopeContext | undefined>(BBEL_SCOPE_KEY, undefined)
const scope = computed<ScopeDescriptor>(() => bbelScopeCtx?.value ?? EMPTY_BBEL_SCOPE)

const entries = ref<Array<{ key: string; value: string }>>([])

function initialize() {
  entries.value = (props.modelValue.bodyFormUrlEncodedContent || []).map((kv) => ({
    key: kv.key,
    value: kv.value,
  }))
  if (entries.value.length === 0) entries.value.push({ key: '', value: '' })
}

function emitUpdate() {
  const pairs = entries.value
    .filter((e) => e.key.trim() !== '')
    .map((e) => ({ key: e.key.trim(), value: e.value }))
  emit('update:modelValue', { $type: 'urlEncoded', bodyFormUrlEncodedContent: pairs })
}

function addRow() {
  entries.value.push({ key: '', value: '' })
}

function removeRow(index: number) {
  entries.value.splice(index, 1)
  if (entries.value.length === 0) entries.value.push({ key: '', value: '' })
  emitUpdate()
}

onMounted(initialize)
</script>

<template>
  <div class="mb-3">
    <label class="form-label">Form Data (URL Encoded)</label>
    <div
      v-for="(entry, index) in entries"
      :key="index"
      class="d-flex align-items-stretch gap-2 mb-2"
    >
      <BbelField
        v-model="entry.key"
        class="w-25 flex-shrink-0"
        :scope="scope"
        single-line
        placeholder="Field name"
        @update:model-value="emitUpdate"
      />
      <BbelField
        v-model="entry.value"
        class="flex-grow-1"
        :scope="scope"
        single-line
        placeholder="Field value"
        @update:model-value="emitUpdate"
      />
      <button
        type="button"
        class="btn btn-outline-danger btn-sm"
        @click="removeRow(index)"
        :disabled="entries.length === 1 && !entry.key && !entry.value"
      >
        <i class="bi bi-trash"></i>
      </button>
    </div>
    <button type="button" class="btn btn-outline-primary btn-sm" @click="addRow">
      <i class="bi bi-plus"></i> Add Field
    </button>
  </div>
</template>
